import { gatewayApi as api } from './gatewayApi';

// Interfaces
export interface NCType {
  Id?: number;
  Name: string;
  Description?: string;
  Active: boolean;
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface NCTypeListResponse {
  data: NCType[];
  total: number;
}

export interface NCTypeSearchParams {
  page?: number;
  pageSize?: number;
  search?: string;
  active?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// API Service
export const nctypeApi = {
  // CRUD Operations
  getAll: (params?: NCTypeSearchParams): Promise<NCTypeListResponse> =>
    api.get('/nctype', params),

  getById: (id: number): Promise<NCType> =>
    api.get(`/nctype/${id}`),

  create: (nctype: Omit<NCType, 'Id'>): Promise<NCType> =>
    api.post('/nctype', nctype),

  update: (id: number, nctype: Partial<NCType>): Promise<NCType> =>
    api.put(`/nctype/${id}`, nctype),

  delete: (id: number): Promise<{ success: boolean; message: string }> =>
    api.delete(`/nctype/${id}`),

  // Search and Filter
  search: (params: NCTypeSearchParams): Promise<NCTypeListResponse> =>
    api.post('/nctype/search', params),

  // Bulk Operations
  bulkActivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/nctype/bulk-activate', { ids }),

  bulkDeactivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/nctype/bulk-deactivate', { ids }),

  bulkDelete: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.post('/nctype/bulk-delete', { ids }),

  // Statistics
  getStatistics: (): Promise<{
    total: number;
    active: number;
    inactive: number;
  }> =>
    api.get('/nctype/statistics'),

  // Export
  exportToCSV: (params?: NCTypeSearchParams): Promise<Blob> =>
    api.getBlob('/nctype/export', { params }),

  // Validation
  validation: {
    validateNCType: (nctype: Partial<NCType>): string[] => {
      const errors: string[] = [];

      // Name validation
      if (!nctype.Name || nctype.Name.trim().length === 0) {
        errors.push('Type Name is required');
      } else if (nctype.Name.trim().length < 2) {
        errors.push('Type Name must be at least 2 characters long');
      } else if (nctype.Name.trim().length > 100) {
        errors.push('Type Name must be less than 100 characters');
      }

      // Description validation (optional but if provided, validate length)
      if (nctype.Description && nctype.Description.trim().length > 500) {
        errors.push('Description must be less than 500 characters');
      }

      // Active validation
      if (nctype.Active === undefined || nctype.Active === null) {
        errors.push('Active status is required');
      }

      return errors;
    },

    validateBulkOperation: (ids: number[]): string[] => {
      const errors: string[] = [];

      if (!ids || ids.length === 0) {
        errors.push('At least one NC Type must be selected');
      }

      if (ids.length > 100) {
        errors.push('Cannot perform bulk operation on more than 100 items at once');
      }

      return errors;
    }
  }
};

export default nctypeApi; 